import fieldMessages from "texts/messages/field";
import regex from "./regex";
import date from "./date";

const fieldValidations = {
    name: (field = 'nome', minLength = 2, maxLength = 30) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            pattern: {
                value: regex.onlyAlphabetic,
                message: fieldMessages.onlyAlphabetic
            }
        }
    },

    /**
     * ID de pixel de rastreamento. O formato depende da plataforma escolhida, e
     * é por isso que `name` não servia: ele exige ao menos uma letra, e o ID da
     * Meta é numérico puro — nenhum pixel da Meta passava no cadastro.
     */
    pixelId: (platform) => {
        return {
            required: fieldMessages.required('ID do pixel'),
            maxLength: {
                value: 255,
                message: fieldMessages.maxValue(255)
            },
            validate: {
                format: (value) => {
                    const format = regex.pixelId[platform];

                    // Sem plataforma escolhida não há formato a cobrar: quem
                    // reclama desse caso é a validação do próprio select.
                    if (!format) {
                        return true;
                    }

                    return format.test((value || '').trim())
                        || fieldMessages.pixelIdFormat(platform);
                }
            }
        }
    },

    /**
     * Token da Conversions API. Opcional e sem formato fixo — cada plataforma
     * emite o seu de um jeito —, mas com teto porque o campo é uma credencial e
     * não um texto livre. O da Meta passa dos 200 caracteres.
     */
    accessToken: () => {
        return {
            maxLength: {
                value: 512,
                message: fieldMessages.maxValue(512)
            }
        }
    },

    /**
     * Nome de pessoa. Separado de `name` porque `name` também valida código de
     * cupom e pergunta de FAQ — onde dígito é legítimo. Aqui a regra pode ser
     * estrita sem quebrar o resto do painel.
     */
    personName: (field = 'nome', minLength = 2, maxLength = 40) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            validate: {
                onlyLetters: (value) => (
                    regex.personName.test((value || '').trim()) || 'Use apenas letras'
                )
            }
        }
    },

    /**
     * Razão social ou nome fantasia. Sem o teto de 30 caracteres e sem a regra
     * de "só letras": "Sociedade Brasileira de Eventos e Produções Ltda" e
     * "Bar do Zé 2 ME" são nomes válidos que a regra anterior recusava.
     */
    companyName: (field = 'razão social', minLength = 2, maxLength = 120) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            }
        }
    },

    generic: (field = 'nome', minLength = 2, maxLength = 1200) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },

        }
    },

    description: (field = 'descrição', maxLength = 120) => {
        return {
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },

        }
    },

    number: (field = 'valor', minLength = 1, maxLength = 15) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            pattern: {
                value: regex.onlyNumber,
                message: fieldMessages.onlyNumber
            }
        }
    },

    quantity: (label = 'Quantidade', maxValue) => ({
        required: `${label} é obrigatória`,
        pattern: {
            value: /^[0-9]+$/,
            message: `${label} deve ser um número`,
        },
        ...(maxValue && {
            max: {
            value: maxValue,
            message: `${label} máxima é ${maxValue}`,
            },
        }),
    }),

    addressNumber: (field = 'nome', minLength = 1, maxLength = 20) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
        }
    },

    link: (field = 'link', minLength = 3, maxLength = 300) => {
        return {
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            pattern: {
                value: regex.link,
                message: fieldMessages.invalidUrl
            }
        }
    },

    date: (field = 'data') => {
        return {
            required: fieldMessages.fillCorrectly(field),
            pattern: {
                value: regex.date,
                message: fieldMessages.invalidFemaleField(field)
            },
            validate: {
                validDateRange: (value) => {
                    const today = new Date();
                    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
                    const hundredYearsLater = new Date();
                    hundredYearsLater.setFullYear(today.getFullYear() + 100);

                    const inputDate = date.getDateWithTime(date.unformat(value))
                    if (!date.isDateValid(inputDate)) {
                        return 'Data inválida'
                    }

                    // if (inputDate < todayDateOnly) {
                    //     return 'A data deve ser maior ou igual ao dia de hoje';
                    // }
                    if (inputDate > hundredYearsLater) {
                        return 'Ano inválido';
                    }
                    return true;
                }
            }
        };
    },

    /**
     * Data de nascimento de quem responde pela conta.
     *
     * A versão anterior exigia data **maior** que hoje (regra de data de evento
     * copiada sem adaptar) e lia uma variável que estava comentada. Ninguém a
     * usava, e o cadastro de parceiro caía no `date()` genérico — que aceitava
     * nascimento em 2030.
     */
    birthdate: (field = 'data de nascimento', minAge = 18, maxAge = 120) => {
        return {
            required: fieldMessages.required(field),
            pattern: {
                value: regex.date,
                message: fieldMessages.invalidFemaleField(field)
            },
            validate: {
                validBirthdate: (value) => {
                    const inputDate = date.getDateWithTime(date.unformat(value))

                    if (!date.isDateValid(inputDate)) {
                        return 'Data inválida'
                    }

                    const today = new Date()
                    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

                    if (inputDate > todayDateOnly) {
                        return 'A data de nascimento não pode estar no futuro'
                    }

                    const minAgeLimit = new Date(todayDateOnly)
                    minAgeLimit.setFullYear(minAgeLimit.getFullYear() - minAge)

                    if (inputDate > minAgeLimit) {
                        return `É preciso ter ao menos ${minAge} anos para abrir uma conta de parceiro`
                    }

                    const maxAgeLimit = new Date(todayDateOnly)
                    maxAgeLimit.setFullYear(maxAgeLimit.getFullYear() - maxAge)

                    if (inputDate < maxAgeLimit) {
                        return 'Confira o ano de nascimento'
                    }

                    return true
                }
            }
        };
    },
    shortDate: (field = 'data') => {
        return {
            required: fieldMessages.fillCorrectly(field),
            pattern: {
                value: regex.shortDate,
                message: fieldMessages.invalidFemaleField(field)
            }
        };
    },

    cardExpiration: (field = 'Data de expiração') => {
        return {
            required: fieldMessages.fillCorrectly(field),
            pattern: {
                value: /^(0[1-9]|1[0-2])\/\d{4}$/,
                message: fieldMessages.invalidFemaleField(field)
            },
            validate: {
                notExpired: (value) => {
                    const [month, year] = value.split('/')
                    const expMonth = parseInt(month)
                    const expYear = parseInt(year)
                    const now = new Date()
                    const currentMonth = now.getMonth() + 1
                    const currentYear = now.getFullYear()

                    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
                        return 'Cartão expirado'
                    }
                    return true
                }
            }
        };
    },

    holderName: (field = 'Nome no cartão', maxLength = 60) => {
        return {
            required: fieldMessages.required(field),
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            pattern: {
                value: regex.onlyAlphabetic,
                message: fieldMessages.onlyAlphabetic
            },
            validate: {
                fullName: (value) => {
                    const names = value.trim().split(/\s+/)
                    if (names.length < 2) {
                        return 'Informe o nome completo (nome e sobrenome)'
                    }
                    return true
                }
            }
        };
    },

    email: (field = 'e-mail') => {
        return {
            required: fieldMessages.required(field),
            pattern: {
                value: regex.email,
                message: fieldMessages.invalidMaleField(field)
            }
        };
    },

    phone: (field = 'telefone') => {
        return {
            required: fieldMessages.required(field),
            pattern: {
                value: regex.phone,
                message: fieldMessages.invalidMaleField(field)
            }
        };
    },

    cpf: (field = 'CPF', required = true) => {
        return {
            ...(required && { required: fieldMessages.required(field) }),
            pattern: {
                value: regex.cpf,
                message: fieldMessages.invalidMaleField(field)
            },
            validate: {
                validCpf: (value) => {
                    if (!required && !value) return true

                    const digits = value.replace(/\D/g, '')
                    if (digits.length !== 11) return `${field} inválido`
                    if (/^(\d)\1{10}$/.test(digits)) return `${field} inválido`

                    let sum = 0
                    for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i)
                    let remainder = (sum * 10) % 11
                    if (remainder === 10) remainder = 0
                    if (remainder !== parseInt(digits[9])) return `${field} inválido`

                    sum = 0
                    for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i)
                    remainder = (sum * 10) % 11
                    if (remainder === 10) remainder = 0
                    if (remainder !== parseInt(digits[10])) return `${field} inválido`

                    return true
                }
            }
        };
    },

    cnpj: (field = 'CNPJ', required = true) => {
        return {
            ...(required && { required: fieldMessages.required(field) }),
            pattern: {
                value: regex.cnpj,
                message: fieldMessages.invalidMaleField(field)
            }
        };
    },

    cpfCnpj: (field = 'CPF/CNPJ', required = true) => {
        return {
            ...(required && { required: fieldMessages.required(field) }),
            validate: {
                validCpfCnpj: (value) => {
                    if (!required && !value) return true

                    const digits = value.replace(/\D/g, '')

                    if (digits.length === 11) {
                        if (/^(\d)\1{10}$/.test(digits)) return `CPF inválido`

                        let sum = 0
                        for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i)
                        let remainder = (sum * 10) % 11
                        if (remainder === 10) remainder = 0
                        if (remainder !== parseInt(digits[9])) return `CPF inválido`

                        sum = 0
                        for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i)
                        remainder = (sum * 10) % 11
                        if (remainder === 10) remainder = 0
                        if (remainder !== parseInt(digits[10])) return `CPF inválido`

                        return true
                    }

                    if (digits.length === 14) {
                        if (/^(\d)\1{13}$/.test(digits)) return `CNPJ inválido`

                        const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
                        const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

                        let sum = 0
                        for (let i = 0; i < 12; i++) sum += parseInt(digits[i]) * weights1[i]
                        let remainder = sum % 11
                        if (remainder < 2) remainder = 0
                        else remainder = 11 - remainder
                        if (remainder !== parseInt(digits[12])) return `CNPJ inválido`

                        sum = 0
                        for (let i = 0; i < 13; i++) sum += parseInt(digits[i]) * weights2[i]
                        remainder = sum % 11
                        if (remainder < 2) remainder = 0
                        else remainder = 11 - remainder
                        if (remainder !== parseInt(digits[13])) return `CNPJ inválido`

                        return true
                    }

                    return `Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido`
                }
            }
        };
    },

    code: (field = 'code', minLength = 6) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.invalidMaleField(field)
            }
        };
    },

    password: (field = 'senha') => {
        return {
            required: fieldMessages.required(field),
            pattern: {
                value: regex.password,
                message: fieldMessages.password(field)
            }
        };
    },

    passwordWithoutPatterns: (field = 'senha') => {
        return {
            required: fieldMessages.required(field),
        };
    },

    cepPatterns: (field = 'CEP', minLength = 9) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.invalidMaleField(field)
            },
            pattern: {
                value: regex.cep,
                message: fieldMessages.fillCorrectly(field)
            }
        };
    },

    time: (field = 'horário') => {
        return {
            required: fieldMessages.required(field),
            pattern: {
                value: /^([01]\d|2[0-3]):([0-5]\d)$/,
                message: fieldMessages.invalidTime(field) 
            }
        };
    },
    
    timeWithoutRequired: (field = 'horario') => {
        return {
            pattern: {
                value: {
                    value: regex.time,
                    message: fieldMessages.invalidTime(field)
                }
            }
        };
    },

    bankAgency: (field = 'agencia', minLength = 4, maxLength = 12) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
        };
    },

    bankAccount: (field = 'conta', minLength = 4, maxLength = 12) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
        };
    },
    
    bankDigit: (field = 'dígito', minLength = 1, maxLength = 1) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
        };
    },

    cvv: (field = 'CVV', minLength = 3, maxLength = 4) => {
        return {
            required: fieldMessages.required(field),
            minLength: {
                value: minLength,
                message: fieldMessages.minValue(minLength)
            },
            maxLength: {
                value: maxLength,
                message: fieldMessages.maxValue(maxLength)
            },
            pattern: {
                value: regex.onlyNumber,
                message: fieldMessages.onlyNumber
            }
        }
    },

    creditCard: (field = 'cartão de crédito') => {
        return {
            required: fieldMessages.required(field),
            validate: {
                validCard: (value) => {
                    const digits = value.replace(/\D/g, '')
                    if (digits.length < 13 || digits.length > 19) return `${field} inválido`

                    let sum = 0
                    let shouldDouble = false
                    for (let i = digits.length - 1; i >= 0; i--) {
                        let digit = parseInt(digits[i])
                        if (shouldDouble) {
                            digit *= 2
                            if (digit > 9) digit -= 9
                        }
                        sum += digit
                        shouldDouble = !shouldDouble
                    }

                    return sum % 10 === 0 || `Número de ${field} inválido`
                }
            }
        };
    },
}

export default fieldValidations;
